import { useState, useEffect } from "react";
import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";
import { ArrowUpRight, MapPin, Mail, Phone } from "lucide-react";
import logo from "../assets/doxzon-logo.png";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../PageRouting/pageRoutes";

export const FOOTER_COLUMNS = [
  {
    title: "Platform",
    links: [
      { label: "TeleConsultation", path: APP_ROUTES.TELEMEDICINE },
      { label: "Health Records", path: APP_ROUTES.HEALTHRECORDS },
      { label: "Diagnostics", path: APP_ROUTES.DIAGNOSTICS },
      { label: "Integrations", path: APP_ROUTES.INTEGRATIONS },
    ],
  },

  {
    title: "Patients",
    links: [
      { label: "Find Care", path: APP_ROUTES.FINDCARE },
      { label: "Appointments", path: APP_ROUTES.APPOINTMENTS },
      { label: "Lab Tests", path: APP_ROUTES.LABTEST },
      { label: "Pharmacy", path: APP_ROUTES.PHARMACY },
      { label: "Family Care", path: APP_ROUTES.FAMILYCARE },
    ],
  },

  {
    title: "Healthcare Professionals",
    links: [
      { label: "EHR / Digital Records", path: APP_ROUTES.EHR },
      { label: "Scheduling", path: APP_ROUTES.SCHEDULING },
      { label: "Virtual Care", path: APP_ROUTES.VIRTUALCARE },
      { label: "e-Prescriptions", path: APP_ROUTES.EPRESCRIPTION },
      { label: "Billing", path: APP_ROUTES.BILLING },
    ],
  },

  {
    title: "Company",
    links: [
      { label: "About Doxzon", path: APP_ROUTES.ABOUTDOXON },
      { label: "Careers", path: APP_ROUTES.CAREERS },
      { label: "Press", path: APP_ROUTES.PRESS },
      { label: "Partners", path: APP_ROUTES.PARTNERS },
      { label: "Contact", path: APP_ROUTES.CONTACT },
      { label: "Blog", path: APP_ROUTES.BLOG },
    ],
  },

  {
    title: "Trust",
    links: [
      { label: "Help Center", path: APP_ROUTES.HELPCENTER },
      { label: "Security & Privacy", path: APP_ROUTES.SECURITY },
      { label: "Compliance", path: APP_ROUTES.COMPLIANCE },
      { label: "System Status", path: APP_ROUTES.SYSTEMSTATUS },
    ],
  },
];

// Links in this map navigate via react-router instead of the default "#" anchor
const FOOTER_LINK_ROUTES = {
  Blog: APP_ROUTES.BLOG,
  "ABDM": APP_ROUTES.PATIENTREGISTRATION,
};

const SOCIAL_ICONS = [
  {
    Icon: FaLinkedin,
    label: "LinkedIn",
    url: "https://www.linkedin.com/company/doxzon/",
  },
  {
    Icon: FaInstagram,
    label: "Instagram",
    url: "https://www.instagram.com/doxzon_healthtech/",
  },
  {
    Icon: FaFacebook,
    label: "Facebook",
    url: "https://www.facebook.com/profile.php?id=61580732167079",
  },

  {
    Icon: FaYoutube,
    label: "YouTube",
    url: "https://www.youtube.com/@Cytozon",
  },
];

const LIGHT = {
  footerBg: "#f8fafc",
  ctaBg: "#ffffff",
  ctaBorder: "#e5e7eb",
  cardBg: "#ffffff",
  border: "#e5e7eb",
  accent: "#18c3ae",
  accentDark: "#032d57",
  headingColor: "#071a3d",
  textPrimary: "#1f2937",
  textMuted: "#6b7280",
  textFaint: "#9ca3af",
  colHeading: "#9ca3af",
  linkColor: "#4b5563",
  linkHover: "#18c3ae",
  inputBg: "#ffffff",
  inputText: "#1f2937",
  inputPlaceholder: "#9ca3af",
  socialBg: "#ffffff",
  socialText: "#6b7280",
  socialHover: "#18c3ae",
  statusDot: "#18c3ae",
  ctaSubtext: "#6b7280",
  iconColor: "#18c3ae",
};

const DARK = {
  footerBg: "#0a0800",
  ctaBg: "#120f02",
  ctaBorder: "rgba(200,150,30,0.30)",
  cardBg: "#110d02",
  border: "rgba(200,150,30,0.25)",
  accent: "#f0c040",
  accentDark: "#c8961e",
  headingColor: "#f5e6b0",
  textPrimary: "#f5e6b0",
  textMuted: "#b89a4a",
  textFaint: "#7a5c10",
  colHeading: "#7a5c10",
  linkColor: "#b89a4a",
  linkHover: "#f0c040",
  inputBg: "#110d02",
  inputText: "#f5e6b0",
  inputPlaceholder: "#7a5c10",
  socialBg: "#110d02",
  socialText: "#b89a4a",
  socialHover: "#f0c040",
  statusDot: "#f0c040",
  ctaSubtext: "#b89a4a",
  iconColor: "#c8961e",
};

const DARK_LOGO_FILTER =
  "brightness(0) invert(1) sepia(1) saturate(4) hue-rotate(5deg) brightness(1.05)";

// Official Google Play multi-color triangle glyph
function PlayStoreIcon({ size = 20 }) {
  return (
    <svg
      viewBox="0 0 512 512"
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      <path fill="#4285F4" d="M130 60 L130 452 L250 256 Z" strokeLinejoin="round" />
      <path fill="#34A853" d="M130 60 L322.5 158.8 L250 256 Z" strokeLinejoin="round" />
      <path fill="#FBBC04" d="M322.5 158.8 L470 256 L322.5 353.2 L250 256 Z" strokeLinejoin="round" />
      <path fill="#EA4335" d="M130 452 L322.5 353.2 L250 256 Z" strokeLinejoin="round" />
    </svg>
  );
}

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

function FooterLink({ link, t, path, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={path || "#"}
      onClick={
        path
          ? (e) => {
            e.preventDefault();
            navigate(path);
          }
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "13px",
        color: hovered ? t.linkHover : t.linkColor,
        textDecoration: "none",
        transition: "color 0.15s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {link}
      <ArrowUpRight
        style={{
          width: 12,
          height: 12,
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.15s",
        }}
      />
    </a>
  );
}

function LegalLink({ link, path, t, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={path}
      onClick={(e) => {
        e.preventDefault();
        navigate(path);
      }}
      style={{
        fontSize: "12px",
        color: hovered ? t.linkHover : t.textFaint,
        textDecoration: "none",
        transition: "color 0.15s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {link}
    </a>
  );
}

const PLAY_STORE_URLS = {
  patient: "https://play.google.com/store/apps/details?id=com.dzpatient",
};

// Compact Google Play badge shown as its own column next to Trust
function AppDownloadBadge({ t, isDark }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div className="footer-app-download">
      <h4
        style={{
          fontSize: "10px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.18em",
          color: t.colHeading,
          marginBottom: "16px",
        }}
      >
        App Download
      </h4>
      <a
        href={PLAY_STORE_URLS.patient}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get the Doxzon app on Google Play"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "10px",
          border: `1px solid ${hovered ? t.accent : t.border}`,
          background: t.cardBg,
          padding: "8px 12px",
          textDecoration: "none",
          transition: "border-color 0.15s, transform 0.15s",
          transform: hovered ? "translateY(-1px)" : "none",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <PlayStoreIcon size={18} />
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
          <span style={{ fontSize: "9px", color: t.textFaint }}>GET IT ON</span>
          <span style={{ fontSize: "12px", fontWeight: 600, color: t.headingColor }}>
            Google Play
          </span>
        </div>
      </a>
    </div>
  );
}

function SocialBtn({ Icon, label, url, t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      style={{
        width: "32px",
        height: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        border: `1px solid ${hovered ? t.socialHover : t.border}`,
        background: t.socialBg,
        color: hovered ? t.socialHover : t.socialText,
        textDecoration: "none",
        transition: "border-color 0.15s, color 0.15s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Icon style={{ width: "14px", height: "14px" }} />
    </a>
  );
}

export function Footer() {
  const navigate = useNavigate();
  const isDark = useDarkMode();
  const t = isDark ? DARK : LIGHT;

  const [demoHovered, setDemoHovered] = useState(false);
  const [consultHovered, setConsultHovered] = useState(false);
  const [subHovered, setSubHovered] = useState(false);

  return (
    <footer
      style={{
        width: "100%",
        background: t.footerBg,
        color: t.textPrimary,
        fontFamily: "system-ui,sans-serif",
        transition: "background 0.3s, color 0.3s",
      }}
    >
      {/* CTA Banner */}
      <div
        style={{
          width: "100%",
          background: t.ctaBg,
          borderTop: `1px solid ${t.ctaBorder}`,
          borderBottom: `1px solid ${t.ctaBorder}`,
          transition: "background 0.3s",
        }}
      >
        <div
          className="footer-cta-inner"
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "2rem 2.5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1.5rem",
              flexWrap: "wrap",
            }}
          >
            <div style={{ maxWidth: "580px" }}>
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  color: t.accent,
                  margin: 0,
                }}
              >
                Technology for Health
              </p>
              <h2
                style={{
                  marginTop: "12px",
                  fontSize: "32px",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: t.headingColor,
                }}
              >
                Care, delivered with confidence.
              </h2>
              <p
                style={{
                  marginTop: "8px",
                  fontSize: "15px",
                  lineHeight: 1.6,
                  color: t.ctaSubtext,
                }}
              >
                Join 12,000+ clinicians using Doxzon to run a calmer, more
                profitable practice.
              </p>
            </div>
            <div
              className="footer-cta-buttons"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => navigate(APP_ROUTES.BOOKDEMO)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "9999px",
                  background: isDark ? "rgba(200,150,30,0.15)" : t.accentDark,
                  border: isDark ? `1.5px solid ${t.accent}` : "none",
                  padding: "10px 22px",
                  fontSize: "14px",
                  color: isDark ? t.accent : "#ffffff",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: demoHovered ? 0.85 : 1,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={() => setDemoHovered(true)}
                onMouseLeave={() => setDemoHovered(false)}
              >
                Request a demo <ArrowUpRight style={{ width: 16, height: 16 }} />
              </button>
              <button
                onClick={() => navigate(APP_ROUTES.CONSULTDOCTOR)}
                style={{
                  borderRadius: "9999px",
                  border: `1.5px solid ${t.accent}`,
                  padding: "10px 22px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: consultHovered
                    ? isDark
                      ? "#0a0800"
                      : "#ffffff"
                    : t.accent,
                  background: consultHovered ? t.accent : "transparent",
                  cursor: "pointer",
                  transition: "background 0.2s, color 0.2s",
                }}
                onMouseEnter={() => setConsultHovered(true)}
                onMouseLeave={() => setConsultHovered(false)}
              >
                Book a consultation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Body */}
      <div
        className="footer-body"
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "3rem 2.5rem",
          display: "grid",
          gridTemplateColumns: "1.3fr repeat(4, 1fr) 1fr",
          gap: "2.5rem",
        }}
      >
        {/* Brand Column */}
        <div className="footer-brand">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "12px",
              borderRadius: "16px",
              border: `1px solid ${t.border}`,
              background: t.cardBg,
              padding: "10px 14px",
              boxShadow: isDark
                ? "0 2px 12px rgba(200,150,30,0.08)"
                : "0 1px 4px rgba(0,0,0,0.06)",
              transition: "background 0.3s, border-color 0.3s",
            }}
          >
            <img
              src={logo}
              alt="Doxzon"
              style={{
                height: "32px",
                width: "auto",
                transition: "filter 0.3s ease",
                filter: isDark ? DARK_LOGO_FILTER : "none",
              }}
            />
          </div>

          <p
            style={{
              marginTop: "16px",
              fontSize: "13px",
              lineHeight: 1.7,
              color: t.textMuted,
              maxWidth: "260px",
            }}
          >
            Doxzon helps you track your vitals seamlessly with advanced digital
            tools. Stay informed and proactive about your health.
          </p>
        </div>

        {/* Nav Columns */}
        {FOOTER_COLUMNS.map((col) => (
          <div key={col.title}>
            <h4
              style={{
                fontSize: "10px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: t.colHeading,
                marginBottom: "16px",
              }}
            >
              {col.title}
            </h4>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {col.links.map((link) => (
                <li key={link.label}>
                  <FooterLink
                    link={link.label}
                    path={link.path}
                    t={t}
                    navigate={navigate}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* App Download — its own column, sitting right beside Trust */}
        <AppDownloadBadge t={t} isDark={isDark} />
      </div>

      {/* Bottom Bar */}
      <div
        className="footer-bottom"
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "20px 2.5rem 32px",
          borderTop: `1px solid ${t.border}`,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div
          className="footer-bottom-left"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: "12px", color: t.textFaint }}>
            &copy; {new Date().getFullYear()} &ndash; Cytozon Technologies Pvt
          </span>
        </div>

        <div
          className="footer-bottom-right"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          {[
            { label: "Privacy", path: APP_ROUTES.PRIVACYPOLICY },
            { label: "Terms", path: APP_ROUTES.TERMSOFUSE },
            { label: "Refund", path: APP_ROUTES.REFUNDPOLICY },
          ].map((item) => (
            <LegalLink
              key={item.label}
              link={item.label}
              path={item.path}
              t={t}
              navigate={navigate}
            />
          ))}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingLeft: "8px",
            }}
          >
            {SOCIAL_ICONS.map(({ Icon, label, url }) => (
              <SocialBtn
                key={label}
                Icon={Icon}
                label={label}
                url={url}
                t={t}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

        @media (max-width: 767px) {

          /* CTA Banner */
          .footer-cta-inner {
            padding: 1.5rem 1.25rem !important;
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .footer-cta-inner h2 {
            font-size: 24px !important;
          }
          .footer-cta-buttons {
            flex-direction: column !important;
            width: 100% !important;
            align-items: stretch !important;
            flex-shrink: unset !important;
          }
          .footer-cta-buttons button {
            width: 100% !important;
            justify-content: center !important;
          }

          /* Footer grid: brand full-width, nav 2-col */
          .footer-body {
            grid-template-columns: 1fr 1fr !important;
            padding: 2rem 1.25rem !important;
            gap: 1.75rem !important;
          }
          .footer-brand {
            grid-column: 1 / -1 !important;
          }
          .footer-app-download {
            grid-column: 1 / -1 !important;
          }

          /* Bottom bar */
          .footer-bottom {
            flex-direction: column !important;
            align-items: flex-start !important;
            padding: 1.25rem 1.25rem 2rem !important;
          }
          .footer-bottom-right {
            gap: 12px !important;
          }
        }

        @media (max-width: 420px) {
          .footer-body {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
  );
}

export default Footer;